import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiRequest from "./AxiosConfiguration";
import { saveMSALConfig } from "../Common/msalConfig";

const settingsSlice = createSlice({
  name: "settings",
  initialState: {
    // Shift Change Over form builder canvas — the API thunks for this
    // feature (fetchFieldTypeList / fetchShiftChangeOverColumnList /
    // saveShiftChangeOverColumns / deleteShiftChangeOverColumn) already
    // live in this slice, so the state they feed lives here too instead of
    // a separate ShiftChangeOverFormBuilder_Slice.js.
    formFields: [],
    prevFormFields: [],
    selectedFieldDetail: null,
    isOptionsRequred: false,
  },
  reducers: {
    setFormFields(state, action) {
      state.formFields = action.payload;
    },
    setPrevFormFields(state, action) {
      state.prevFormFields = action.payload;
    },
    setSelectedFieldDetail(state, action) {
      state.selectedFieldDetail = action.payload;
    },
    setIsOptionsRequred(state, action) {
      state.isOptionsRequred = action.payload;
    },
  },
});

export const {
  setFormFields,
  setPrevFormFields,
  setSelectedFieldDetail,
  setIsOptionsRequred,
} = settingsSlice.actions;

export default settingsSlice.reducer;

// ── Dropdown ──────────────────────────────────
export const fetchSettingsDropdownValues = createAsyncThunk(
  "settings/fetchDropdownValues",
  async () =>
    await apiRequest("GET", "Settings/Settings_TwoColumnDropdownList"),
);

export const fetchBasicStandards = createAsyncThunk(
  "settings/fetchBasicStandards",
  async ({ term = "BASICSTANDARD", companyID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `Settings/Settings_TwoColumnList?term=${encodeURIComponent(term)}&companyID=${companyID}`,
    ),
);

export const fetchSettingsStats = createAsyncThunk(
  "settings/fetchStats",
  async () => await apiRequest("GET", "Settings/Settings_TwoColumnList_Count"),
);

export const saveTwoColumnListEntry = createAsyncThunk(
  "settings/saveTwoColumnListEntry",
  async (data) =>
    await apiRequest(
      "POST",
      "Settings/Settings_TwoColumnList_CreateUpdate",
      data,
      true,
    ),
);

export const updateActivitySortOrder = createAsyncThunk(
  "settings/updateActivitySortOrder",
  async (data) =>
    await apiRequest(
      "POST",
      "Settings/Settings_Activity_UpdateSortOrder",
      data,
      true,
    ),
);

export const updateFacilitySortOrder = createAsyncThunk(
  "settings/updateFacilitySortOrder",
  async (data) =>
    await apiRequest(
      "POST",
      "Settings/Settings_Facility_UpdateSortOrder",
      data,
      true,
    ),
);

export const deleteTwoColumnListEntry = createAsyncThunk(
  "settings/deleteTwoColumnListEntry",
  async ({ term, id, modifiedBy = 0, companyID = 0 }) =>
    await apiRequest(
      "POST",
      `Settings/Settings_TwoColumnList_Delete?term=${encodeURIComponent(term)}&ID=${id}&modifiedBy=${modifiedBy}&companyID=${companyID}`,
    ),
);

// ── Test Type ─────────────────────────────────
export const fetchTestTypes = createAsyncThunk(
  "settings/fetchTestTypes",
  async ({ ID = 0, companyID } = {}) =>
    await apiRequest(
      "GET",
      `Settings/Settings_GetTestTypeList?ID=${ID}&companyID=${companyID}`,
    ),
);

export const saveTestType = createAsyncThunk(
  "settings/saveTestType",
  async (data) =>
    await apiRequest(
      "POST",
      "Settings/Settings_TestType_CreateUpdate",
      data,
      true,
    ),
);

// ── Facility ──────────────────────────────────
export const fetchFacilities = createAsyncThunk(
  "settings/fetchFacilities",
  async ({ ID = 0, companyID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `Settings/Settings_GetFacilityList?ID=${ID}&companyID=${companyID}`,
    ),
);

export const saveFacility = createAsyncThunk(
  "settings/saveFacility",
  async (data) =>
    await apiRequest(
      "POST",
      "Settings/Settings_Facility_CreateUpdate",
      data,
      true,
    ),
);

// ── Location ──────────────────────────────────
export const fetchLocations = createAsyncThunk(
  "settings/fetchLocations",
  async ({ ID = 0, companyID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `Settings/Settings_GetLocationList?ID=${ID}&companyID=${companyID}`,
    ),
);

export const saveLocation = createAsyncThunk(
  "settings/saveLocation",
  async (data) =>
    await apiRequest(
      "POST",
      "Settings/Settings_Location_CreateUpdate",
      data,
      true,
    ),
);

export const deleteLocation = createAsyncThunk(
  "settings/deleteLocation",
  async ({ id, modifiedBy = 0, companyID = 0 }) =>
    await apiRequest(
      "POST",
      `Settings/Settings_Location_Delete?ID=${id}&modifiedBy=${modifiedBy}&companyID=${companyID}`,
    ),
);

// GET Settings/Settings_GetCityList?companyID=X
export const fetchCityList = createAsyncThunk(
  "settings/fetchCityList",
  async ({ companyID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `Settings/Settings_GetCityList?companyID=${companyID}`,
    ),
);

// ── Country / State / City ────────────────────
export const fetchCountries = createAsyncThunk(
  "settings/fetchCountries",
  async () => await apiRequest("GET", "CommonHelperDefaultData/GetCountryList"),
);

export const fetchStates = createAsyncThunk(
  "settings/fetchStates",
  async ({ countryId = 0 } = {}) =>
    await apiRequest(
      "GET",
      `CommonHelperDefaultData/GetStateList?countryId=${countryId}`,
    ),
);

export const fetchCities = createAsyncThunk(
  "settings/fetchCities",
  async ({ stateId = 0 } = {}) =>
    await apiRequest(
      "GET",
      `CommonHelperDefaultData/GetCityList?stateId=${stateId}`,
    ),
);

// ── Holiday ───────────────────────────────────

// GET  Settings/Settings_GetHolidayList?ID=0&companyID=X
export const fetchHolidays = createAsyncThunk(
  "settings/fetchHolidays",
  async ({ ID = 0, companyID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `Settings/Settings_GetHolidayList?ID=${ID}&companyID=${companyID}`,
    ),
);

// GET  Settings/Settings_GetHolidayTypeList?ID=0&companyID=X
// Called when the Add/Edit drawer opens to populate the Holiday Type dropdown
export const fetchHolidayTypes = createAsyncThunk(
  "settings/fetchHolidayTypes",
  async ({ ID = 0, companyID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `Settings/Settings_GetHolidayTypeList?ID=${ID}&companyID=${companyID}`,
    ),
);

// POST Settings/Settings_Holiday_CreateUpdate
// Body: { HolidayType: { HolidayTypeID, HolidayTypeName, HolidayDate }, CreatedBy, CompanyID }
export const saveHoliday = createAsyncThunk(
  "settings/saveHoliday",
  async (data) =>
    await apiRequest(
      "POST",
      "Settings/Settings_Holiday_CreateUpdate",
      data,
      true,
    ),
);
// ── Shift ─────────────────────────────────────

// GET  Settings/Settings_GetShiftList?ID=0&companyID=X
export const fetchShifts = createAsyncThunk(
  "settings/fetchShifts",
  async ({ ID = 0, companyID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `Settings/Settings_GetShiftList?ID=${ID}&companyID=${companyID}`,
    ),
);

// POST Settings/Settings_Shift_CreateUpdate
// Body: { CompanyID, CreatedBy, Shifts: [{ ShiftTimingId, ShiftNo, ShiftName, ShiftStartTime, ShiftEndTime }] }
export const saveShifts = createAsyncThunk(
  "settings/saveShifts",
  async (data) =>
    await apiRequest(
      "POST",
      "Settings/Settings_Shift_CreateUpdate",
      data,
      true,
    ),
);

// ── Shift Change Over ─────────────────────────
// GET  Settings/Get_FieldTypeList
export const fetchFieldTypeList = createAsyncThunk(
  "settings/fetchFieldTypeList",
  async () => await apiRequest("GET", "Settings/Get_FieldTypeList"),
);

// GET Settings/Get_TempHumidityConfigList — the 4 popup-frequency options
// (TempHumidityConfig_Mst), used by the Temperature & Humidity config tab.
export const fetchTempHumidityConfigList = createAsyncThunk(
  "settings/fetchTempHumidityConfigList",
  async () =>
    await apiRequest("GET", "Settings/Get_TempHumidityConfigList"),
);

// GET Settings/Get_TempHumidityConfigByCompany?companyId=X — whichever
// frequency is currently saved for this company (Settings.TempHumidityConfigID
// joined back to TempHumidityConfig_Mst), null if nothing's been saved yet.
export const fetchTempHumidityConfigByCompany = createAsyncThunk(
  "settings/fetchTempHumidityConfigByCompany",
  async ({ companyId }) =>
    await apiRequest(
      "GET",
      `Settings/Get_TempHumidityConfigByCompany?companyId=${companyId}`,
    ),
);

// POST Settings/Save_TempHumidityConfig?companyId=X&tempHumidityConfigID=X
// Upserts Settings.TempHumidityConfigID for the company (SP handles
// UPDATE-if-exists vs INSERT) — tempHumidityConfigID is the selected
// option's FrequencyID from TempHumidityConfig_Mst.
export const saveTempHumidityConfig = createAsyncThunk(
  "settings/saveTempHumidityConfig",
  async ({ companyId, tempHumidityConfigID }) =>
    await apiRequest(
      "POST",
      `Settings/Save_TempHumidityConfig?companyId=${companyId}&tempHumidityConfigID=${tempHumidityConfigID}`,
    ),
);

// GET  Settings/Get_ShiftChangeOverColumnList?companyID=X
export const fetchShiftChangeOverColumnList = createAsyncThunk(
  "settings/fetchShiftChangeOverColumnList",
  async ({ companyID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `Settings/Get_ShiftChangeOverColumnList?companyID=${companyID}`,
    ),
);

// POST Settings/Save_ShiftChangeOverColumns
// Body: { CompanyID, CreatedBy, Columns: [{ Id, Value, FieldTypeID, IsRequired, Options }] }
export const saveShiftChangeOverColumns = createAsyncThunk(
  "settings/saveShiftChangeOverColumns",
  async (data) =>
    await apiRequest("POST", "Settings/Save_ShiftChangeOverColumns", data),
);

// POST Settings/Delete_ShiftChangeOverColumn?id=X&companyID=X&modifiedBy=X
// Soft-deletes the column and cascades to its options row(s) server-side.
export const deleteShiftChangeOverColumn = createAsyncThunk(
  "settings/deleteShiftChangeOverColumn",
  async ({ id, companyID = 0, modifiedBy = 0 }) =>
    await apiRequest(
      "POST",
      `Settings/Delete_ShiftChangeOverColumn?id=${id}&companyID=${companyID}&modifiedBy=${modifiedBy}`,
    ),
);
// ── Email Config ──────────────────────────────
export const fetchEmailConfig = createAsyncThunk(
  "settings/fetchEmailConfig",
  async ({ companyID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `Settings/Settings_GetEmail_SharedFolder_Config?companyID=${companyID}`,
    ),
);

// export const saveEmailConfig = createAsyncThunk(
//   "settings/saveEmailConfig",
//   async (data) =>
//     await apiRequest("POST", "Settings/Settings_Email_SharedFolderConfig_CreateUpdate", data, true)
// );
export const fetchMailMethods = createAsyncThunk(
  "settings/fetchMailMethods",
  async ({ companyID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `Settings/Settings_GetMailMethodList?companyID=${companyID}`,
    ),
);
export const fetchMailConfigDetails = createAsyncThunk(
  "settings/fetchMailConfigDetails",
  async ({ companyID = 0, mailMethodID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `Settings/Settings_GetMailConfigDetails?companyID=${companyID}&mailMethodID=${mailMethodID}`,
    ),
);
export const saveEmailConfig = createAsyncThunk(
  "settings/saveEmailConfig",
  async (data) =>
    await apiRequest(
      "POST",
      "Settings/Settings_SaveEmailConfigSettings",
      data,
      true,
    ),
);
// ── Drive Config ──────────────────────────────
export const fetchSyncAvailable = createAsyncThunk(
  "settings/fetchSyncAvailable",
  async ({ companyID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `Settings/Settings_GetSyncAvailable?companyID=${companyID}`,
    ),
);

export const fetchDriveConfig = createAsyncThunk(
  "settings/fetchDriveConfig",
  async ({ companyID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `Settings/Settings_GetDriveConfig?companyID=${companyID}`,
    ),
);

export const saveDriveConfig = createAsyncThunk(
  "settings/saveDriveConfig",
  async (data) =>
    await apiRequest("POST", "Settings/Settings_SaveDriveConfig", data, true),
);

// ── Save MSAL config (Admin → Application Config → Credentials) ──────────────
export const saveMSALConfiguration = createAsyncThunk(
  "sso/saveMSALConfiguration",
  async ({ companyID, employeeID, msalConfig }, { rejectWithValue }) => {
    try {
      const res = await apiRequest(
        "POST",
        "Settings/Settings_SaveMSALConfig",
        {
          CompanyID: companyID,
          CreatedBy: employeeID,
          ClientId: msalConfig.clientId,
          Authority: msalConfig.authority,
          RedirectUri: msalConfig.redirectUri,
          AllowedDomain: msalConfig.allowedDomain,
        },
        true, // forSave — shows success toast via AxiosConfiguration
        false,
        false,
        false,
      );

      if (res?.success) {
        // Cache in localStorage so the login page can use it immediately
        saveMSALConfig(msalConfig);
      }

      return res;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

// ── Fetch MSAL config from backend on app startup ────────────────────────────
export const fetchMSALConfiguration = createAsyncThunk(
  "sso/fetchMSALConfiguration",
  async ({ companyID }, { rejectWithValue }) => {
    try {
      const res = await apiRequest(
        "GET",
        `Settings/Settings_GetMSALConfig?companyID=${companyID}`,
      );

      console.log("Fetch Response", res);

      // response itself may already be array
      const data = res?.data || res;

      if (Array.isArray(data) && data.length > 0) {
        const d = data[0];

        return {
          clientId: d.ClientID ?? d.ClientId ?? "",
          authority: d.Authority ?? "",
          redirectUri: d.RedirectURI ?? d.RedirectUri ?? "",
          allowedDomain: (d.AllowedDomain ?? "").toLowerCase(),
        };
      }

      return null;
    } catch (err) {
      console.log("Fetch Error", err);
      return rejectWithValue(err);
    }
  },
);

export const fetchMSALConfigByDomain = createAsyncThunk(
  "sso/fetchMSALConfigByDomain",
  async ({ domain }, { rejectWithValue }) => {
    try {
      const res = await apiRequest(
        "GET",
        `Auth/GetMSALConfigByDomain?domain=${encodeURIComponent(domain)}`,
      );

      const data = res?.data || res;
      const d = Array.isArray(data) ? data[0] : data;

      if (!d || !d.ClientID) return null; // domain not registered → no SSO for this company

      const config = {
        clientId: d.ClientID ?? d.ClientId ?? "",
        authority: d.Authority ?? "",
        redirectUri: d.RedirectURI ?? d.RedirectUri ?? "",
        allowedDomain: (d.AllowedDomain ?? domain).toLowerCase(),
      };

      // Cache so the login page can call buildMicrosoftLoginURL() immediately
      saveMSALConfig(config);

      return config;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

export const SaveCheckListConfig = createAsyncThunk(
  "settings/SaveCheckListConfig",
  async (data) =>
    await apiRequest("POST", "Settings/SaveCheckListConfig", data, true),
);

export const GetCheckListConfig = createAsyncThunk(
  "settings/GetCheckListConfig",
  async ({ companyID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `Settings/GetCheckListConfig?companyID=${companyID}`,
    ),
);

export const DeleteCheckListConfig = createAsyncThunk(
  "settings/DeleteCheckListConfig",
  async ({ id = 0, modifiedBy = 0 } = {}) =>
    await apiRequest(
      "GET",
      `Settings/DeleteCheckListConfig?id=${id}&modifiedBy=${modifiedBy}`,
    ),
);

// ── Date Format Config ────────────────────────

// GET  Settings/GetDateFormat
// Returns the master list of supported date formats
// (e.g. [{ DateFormatID: 1, DateFormat: "dd-MM-yyyy" }, ...])
export const fetchDateFormatList = createAsyncThunk(
  "settings/fetchDateFormatList",
  async () => await apiRequest("GET", "Settings/GetDateFormat"),
);

// GET  Settings/GetDateFormatConfig?companyID=X
// Returns the format currently saved for this company (null if none saved yet)
export const fetchDateFormatConfig = createAsyncThunk(
  "settings/fetchDateFormatConfig",
  async ({ companyID = 0 } = {}) =>
    await apiRequest(
      "GET",
      `Settings/GetDateFormatConfig?companyID=${companyID}`,
    ),
);

// POST Settings/SaveDateFormat?CompanyId=X&EmpId=Y
// Body (camelCase) — matches Swagger schema exactly:
//   { dateFormat: "dd-MM-yyyy", dateFormatId: 1 }
// CompanyId and EmpId are query-string params; body is the DateFormatConfigModel.
export const saveDateFormatConfig = createAsyncThunk(
  "settings/saveDateFormatConfig",
  async ({ CompanyID = 0, CreatedBy = 0, DateFormatID, DateFormat }) =>
    await apiRequest(
      "POST",
      `Settings/SaveDateFormat?CompanyId=${CompanyID}&EmpId=${CreatedBy}`,
      { dateFormat: DateFormat, dateFormatId: Number(DateFormatID) },
      true,
    ),
);
